import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';

import DeleteModal from '@atom/components/common/DeleteModal';
import LocationsAndAssetsContext from '@atom/components/common/workOrderDetail/locationsAndAssetsSection/LocationsAndAssetsContext';
import RequiredFieldsConfirmModal from '@atom/components/common/workOrderDetail/requiredFieldsConfirmModal/RequiredFieldsConfirmModal';
import { TASK_ASSET_REMOVE } from '@atom/graph/task';
import { getStaticImage } from '@atom/map';
import { Icon, IconButton, Snackbar } from '@atom/mui';
import { DeleteIcon } from '@atom/mui/icons';
import { AssetConnectionItem } from '@atom/types/asset';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { FILES_ENDPOINT } from '@atom/utilities/endpoints';
import { getSchemaIconFromMarkerId } from '@atom/utilities/schemaUtilities';
import { isAldotTaskLocationEnabled } from '@atom/utilities/tenantFeatureUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AldotAttributeSection from './AldotAttributeSection';

import classes from './locationsAndAssetsSection.module.css';

const styles = {
  icon: {
    height: '28px',
    width: '28px',
    marginRight: '5px',
  },
};

interface Props {
  asset: AssetConnectionItem;
  setHoverId: (id: string) => void;
  removeAsset?: (id: string) => void;
  selectedAssetId?: string;
  setSelectedAssetId?: (id: string) => void;
}

const TaskAssetRow = ({
  asset,
  removeAsset,
  setHoverId,
  selectedAssetId,
  setSelectedAssetId,
}: Props) => {
  const { workOrderDetail, task, refetch, dataCySuffix } = useContext(
    LocationsAndAssetsContext,
  );

  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openFieldsConfirm, setOpenFieldsConfirm] = useState<boolean>(false);

  const [deleteAsset, { loading: loadingDelete }] =
    useMutation(TASK_ASSET_REMOVE);

  const handleDelete = async () => {
    setOpenDelete(false);
    Snackbar.info({ message: `Deleting ${asset.name}...` });

    try {
      await deleteAsset({
        variables: {
          input: {
            workOrderId: workOrderDetail.id,
            taskId: task.id,
            assetId: asset.id,
          },
        },
      });

      refetch();

      Snackbar.info({ message: `Deleted ${asset.name}.` });
    } catch (error) {
      Snackbar.error({ message: `Failed to delete ${asset.name}.` });
    }
  };

  const handleDeleteClick = () => {
    if (removeAsset) {
      return removeAsset(asset?.id);
    }

    if (
      task.isCompleted &&
      (task?.requireAtLeastOneMultiAsset ||
        !isNilOrEmpty(task?.requiredMultiAssetSchemaIds))
    ) {
      return setOpenFieldsConfirm(true);
    }

    return setOpenDelete(true);
  };

  const onProceedComplete = async () => {
    await refetch();

    setOpenFieldsConfirm(false);
    setOpenDelete(true);
  };

  const getSummaryImage = () => {
    if (!asset) {
      return '';
    }

    return !asset.mainPhotoFileId
      ? getStaticImage(asset.location, '60x60')
      : `${FILES_ENDPOINT}/${asset.mainPhotoFileId}_180`;
  };

  const isEditDisabled =
    workOrderDetail.isClosed || doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR);
  const showDeleteButton = !isEditDisabled;

  const onAssetClick = () => {
    if (setSelectedAssetId) {
      setSelectedAssetId(asset?.id);
      setHoverId(asset?.id);
    }
  };

  const onMouseLeave = () => {
    if (isNilOrEmpty(selectedAssetId)) {
      setHoverId('');
    }
  };

  const schemaName = asset?.assetType || asset?.schemaName;

  return (
    <>
      <div
        className={classes.assetRow}
        onMouseEnter={() => setHoverId(asset?.id)}
        onMouseLeave={onMouseLeave}
      >
        <div className={classes.assetContent} onClick={onAssetClick}>
          <div
            className={classes.assetImage}
            style={{
              backgroundImage: `url('${getSummaryImage()}')`,
            }}
          />
          <div>
            <div className={classes.assetName}>{asset?.name}</div>
            <div className={classes.schemaSection}>
              <img
                style={styles.icon}
                src={getSchemaIconFromMarkerId(asset?.markerId)}
              />
              <div className={classes.assetSubtext}>{schemaName}</div>
            </div>
            {isAldotTaskLocationEnabled && (
              <AldotAttributeSection attributes={asset?.attributes} />
            )}
          </div>
        </div>
        <div className={classes.actionButtons}>
          {showDeleteButton && (
            <IconButton
              onClick={handleDeleteClick}
              disabled={loadingDelete || isEditDisabled}
              data-cy={`taskMapAssetDelete-${dataCySuffix}`}
            >
              <Icon icon={DeleteIcon} />
            </IconButton>
          )}
        </div>
      </div>
      <DeleteModal
        open={openDelete}
        onConfirm={handleDelete}
        onCancel={() => setOpenDelete(false)}
        title="Remove Asset?"
        content="Are you sure you want to remove this asset?"
        cancelText="Cancel"
        confirmText="Remove"
      />
      <RequiredFieldsConfirmModal
        open={openFieldsConfirm}
        onCancel={() => setOpenFieldsConfirm(false)}
        onComplete={onProceedComplete}
        task={task}
        workOrderId={workOrderDetail?.id}
      />
    </>
  );
};

export default TaskAssetRow;
